// Theme "Variante B": semantische Design-Tokens (siehe .github/design-system.instructions.md).
import { createTheme } from '@mui/material/styles';

export const tokens = {
  color: {
    text: {
      primary: '#111827',
      secondary: '#374151',
      muted: '#6B7280',
      subtle: '#9CA3AF',
    },
    border: {
      default: '#E5E7EB',
      divider: '#F3F4F6',
      strong: '#D1D5DB',
      focus: '#111827',
    },
    surface: {
      default: '#FFFFFF',
      subtle: '#F3F4F6',
    },
    accent: {
      default: '#2150E0',
      text: '#1B3FB5',
      border: '#C7D5FF',
      surface: '#F5F8FF',
    },
    warning: {
      text: '#92400E',
      surface: '#FEF3C7',
      star: '#D97706',
    },
    danger: {
      text: '#B42318',
    },
  },
  radius: {
    card: 16,
    input: 12,
    button: 10,
    row: 10,
    pill: 99,
    badge: 5,
  },
  shadow: {
    hero: '0 1px 2px rgba(17,24,39,0.04), 0 8px 24px rgba(17,24,39,0.06)',
    heroMobile: '0 1px 2px rgba(17,24,39,0.04), 0 6px 18px rgba(17,24,39,0.06)',
    heroFocus: '0 12px 40px rgba(17,24,39,0.12)',
    dropdown: '0 20px 60px rgba(17,24,39,0.14)',
  },
  layout: {
    topBar: 64,
    topBarMobile: 56,
    tabBar: 72,
    pageMobileGutter: 20,
    containerToday: 920,
    containerObject: 760,
    containerAssistant: 420,
    containerData: 880,
  },
  focusRing: '2px solid #2150E0',
};

const { color: c, radius: r } = tokens;

const fontFamily =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const theme = createTheme({
  tokens,
  palette: {
    primary: {
      main: c.accent.default,
      light: c.accent.surface,
      dark: c.accent.text,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: c.text.secondary,
      light: c.text.muted,
      dark: c.text.primary,
      contrastText: '#FFFFFF',
    },
    success: { main: '#16A34A', light: '#4ade80', dark: '#15803d' },
    warning: { main: c.warning.star, light: c.warning.surface, dark: c.warning.text },
    error: { main: c.danger.text, light: '#f87171', dark: '#7A1A12' },
    info: { main: c.accent.default, light: c.accent.surface, dark: c.accent.text },
    background: {
      default: c.surface.default,
      paper: c.surface.default,
      elevated: c.surface.default,
    },
    text: {
      primary: c.text.primary,
      secondary: c.text.muted,
      disabled: c.text.subtle,
      muted: c.text.muted,
      subtle: c.text.subtle,
    },
    divider: c.border.divider,
    action: {
      hover: c.surface.subtle,
      selected: c.surface.subtle,
    },
  },
  typography: {
    fontFamily,
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontSize: '2rem', lineHeight: '40px', fontWeight: 500, letterSpacing: '-0.025em' },
    h2: { fontSize: '1.75rem', lineHeight: '34px', fontWeight: 500, letterSpacing: '-0.03em' },
    h3: { fontSize: '1.375rem', lineHeight: '28px', fontWeight: 500, letterSpacing: '-0.02em' },
    h4: { fontSize: '1.125rem', lineHeight: '24px', fontWeight: 500 },
    h5: { fontSize: '1rem', lineHeight: '22px', fontWeight: 500 },
    h6: { fontSize: '0.9375rem', lineHeight: '20px', fontWeight: 500 },
    subtitle1: { fontSize: '0.9375rem', lineHeight: '20px', fontWeight: 500 },
    subtitle2: { fontSize: '0.8125rem', lineHeight: '18px', fontWeight: 500 },
    body1: { fontSize: '0.9375rem', lineHeight: '20px', fontWeight: 400 },
    body2: { fontSize: '0.8125rem', lineHeight: '18px', fontWeight: 400 },
    button: { fontSize: '0.875rem', lineHeight: '20px', fontWeight: 500, textTransform: 'none' },
    caption: { fontSize: '0.75rem', lineHeight: '16px', fontWeight: 400 },
    overline: {
      fontSize: '0.8125rem',
      lineHeight: '18px',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: 0,
    },
  },
  shape: { borderRadius: r.button },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontVariantNumeric: 'tabular-nums',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: c.border.strong, borderRadius: '4px' },
        },
        'input, textarea, select': { fontVariantNumeric: 'tabular-nums' },
        // Sichtbarer Fokusring (Annahme, nicht in den Entwürfen gezeichnet)
        ':focus-visible': { outline: tokens.focusRing, outlineOffset: 2 },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: r.button,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          backgroundColor: c.text.primary,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#000000' },
        },
        containedPrimary: {
          backgroundColor: c.text.primary,
          '&:hover': { backgroundColor: '#000000' },
        },
        outlined: {
          borderWidth: 1,
          borderColor: c.border.default,
          color: c.text.secondary,
          '&:hover': { borderWidth: 1, backgroundColor: c.surface.subtle, borderColor: c.border.strong },
        },
        text: {
          color: c.text.secondary,
          '&:hover': { backgroundColor: c.surface.subtle },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: r.card,
          border: `1px solid ${c.border.default}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: { boxShadow: 'none', border: `1px solid ${c.border.default}` },
        elevation2: { boxShadow: 'none', border: `1px solid ${c.border.default}` },
        elevation3: { boxShadow: tokens.shadow.dropdown },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: c.surface.default,
          color: c.text.primary,
          borderBottom: `1px solid ${c.border.divider}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: r.input,
          backgroundColor: c.surface.default,
          '& fieldset': { borderColor: c.border.default, borderWidth: 1 },
          '&:hover fieldset': { borderColor: c.border.strong },
          '&.Mui-focused fieldset': { borderColor: c.border.focus, borderWidth: 1.5 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: r.pill,
          fontSize: '0.8125rem',
          fontWeight: 500,
        },
        outlined: {
          borderColor: c.border.default,
          color: c.text.secondary,
        },
        filled: {
          backgroundColor: c.surface.subtle,
          color: c.text.secondary,
          '&:hover': { backgroundColor: c.border.default },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${c.border.divider}`, minHeight: 44 },
        indicator: { backgroundColor: c.text.primary, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 44,
          color: c.text.muted,
          '&.Mui-selected': { color: c.text.primary },
          '&:hover': { color: c.text.primary, backgroundColor: 'transparent' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: r.button,
          color: c.text.secondary,
          '&:hover': { backgroundColor: c.surface.subtle },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: r.card, boxShadow: tokens.shadow.dropdown },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: r.input, boxShadow: tokens.shadow.dropdown },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: r.input, fontSize: '0.875rem' },
        standardSuccess: { backgroundColor: '#F0F9F4', color: '#057642', border: '1px solid #D1F2DF' },
        standardError: { backgroundColor: '#FEF2F2', color: c.danger.text, border: '1px solid #FECACA' },
        standardWarning: { backgroundColor: c.warning.surface, color: c.warning.text, border: '1px solid #FDE68A' },
        standardInfo: { backgroundColor: c.accent.surface, color: c.accent.text, border: `1px solid ${c.accent.border}` },
      },
    },
    MuiListItem: {
      styleOverrides: { root: { borderRadius: r.row } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: r.row,
          '&:hover': { backgroundColor: c.surface.subtle },
          '&.Mui-selected': {
            backgroundColor: c.surface.subtle,
            '&:hover': { backgroundColor: c.surface.subtle },
          },
        },
      },
    },
  },
});

export default theme;
